import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../../store/redux/store/store';
import AppIcon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';
import { Switch } from '../../../../components/ui/Switch';
import { Skeleton } from '../../../../components/ui/Skeleton';
import { cn } from '../../../../utils/cn';
import { useNotificationModules } from '../../../../hooks/useNotificationModules';
import type { NotificationModuleView } from '../../../../api/General/NotificationModules';
import {
  cadenceLabel,
  channelLabel,
  moduleSummary,
  moduleTitle,
  typeLabel,
} from '../../../../components/notifications/notificationModuleCopy';

/**
 * CO-375 / owner decision E13 — the ONE place a company's notification modules and its master switch
 * are changed (the master switch moved here from Settings > Attendance > Work schedule config and from
 * the Operator company-rules page). Each switch applies immediately, like every other toggle on this
 * page family; there is no page-wide save because each row is an independent, recorded answer.
 *
 * Owner E13 (2026-09-28): HR answers module opt-ins. The writes (module state, master switch) are HrOnly on
 * the server, so an Admin sees this tab READ-ONLY: every switch and confirm action is disabled/hidden and a
 * note says who can change it.
 */
const NotificationModulesTab: React.FC = () => {
  const { t, i18n } = useTranslation();
  const role = useSelector((state: RootState) => state.user.role) as string | undefined;
  const canEdit = (role ?? '').toLowerCase() === 'hr';
  // fresh: this tab is the authority for the master switch and the test-company mark, both of which another
  // person can change after MasterLayout's prompt cached them (review-3 finding 3), so it always refetches.
  const { data, loading, failed, reload, setModuleState, setMasterDisabled } = useNotificationModules(true, {
    fresh: true,
  });
  const [busy, setBusy] = useState<string | null>(null);
  const [rowError, setRowError] = useState<{ key: string; message: string } | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [confirmMasterOff, setConfirmMasterOff] = useState(false);

  const toggleExpanded = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const changeModule = async (module: NotificationModuleView, on: boolean) => {
    setBusy(module.key);
    setRowError(null);
    const err = await setModuleState(module.key, on ? 'On' : 'Off', 'settings');
    setBusy(null);
    if (err) setRowError({ key: module.key, message: t('notificationModules.prompt.error') });
  };

  const changeMaster = async (disabled: boolean) => {
    setBusy('master');
    setRowError(null);
    const err = await setMasterDisabled(disabled);
    setBusy(null);
    setConfirmMasterOff(false);
    if (err) setRowError({ key: 'master', message: t('notificationModules.prompt.error') });
  };

  if (loading && !data) {
    return (
      <div className="space-y-4" aria-busy="true">
        <Skeleton className="h-20 w-full rounded-lg" />
        <div className="divide-y divide-border rounded-lg border border-border">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex items-center justify-between gap-4 px-4 py-4">
              <div className="space-y-2">
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-3 w-64" />
              </div>
              <Skeleton className="h-6 w-11 rounded-full" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (failed || !data) {
    return (
      <div className="flex flex-col items-start gap-3 rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm">
        <p className="flex items-center gap-2 text-destructive">
          <AppIcon name="AlertCircle" size={16} className="shrink-0" />
          {t('Failed to load settings')}
        </p>
        <Button variant="outline" size="sm" iconName="RefreshCw" onClick={() => void reload()}>
          {t('Retry')}
        </Button>
      </div>
    );
  }

  const masterOn = !data.masterDisabled;

  return (
    <div className="space-y-6" data-testid="e13-modules-tab" data-readonly={canEdit ? undefined : 'true'}>
      {!canEdit && (
        <p
          className="inline-flex max-w-[70ch] items-center gap-2 text-sm text-muted-foreground"
          data-testid="e13-modules-readonly-note"
        >
          <AppIcon name="Lock" size={14} className="shrink-0" />
          {t('notificationModules.readOnlyAdmin')}
        </p>
      )}
      {/* Master switch — framed on its own: it overrides everything below it. */}
      <section
        aria-labelledby="e13-master-title"
        className={cn(
          'rounded-lg border p-4',
          masterOn ? 'border-border bg-muted/30' : 'border-amber-500/40 bg-amber-500/10'
        )}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 space-y-1">
            <h3 id="e13-master-title" className="text-sm font-semibold text-foreground">
              {t('notificationModules.master.title')}
            </h3>
            <p className="max-w-[70ch] text-sm text-muted-foreground" data-testid="e13-master-state">
              {masterOn
                ? t('notificationModules.master.on')
                : data.isTestCompany
                  ? t('notificationModules.master.offTestCompany')
                  : t('notificationModules.master.off')}
            </p>
            {data.isTestCompany && (
              <p
                className="inline-flex max-w-[70ch] items-center gap-1.5 text-xs font-medium text-foreground"
                data-testid="e13-test-company-note"
              >
                <AppIcon name="FlaskConical" size={14} className="shrink-0 text-muted-foreground" />
                {t('notificationModules.master.testCompany')}
              </p>
            )}
          </div>
          <Switch
            checked={masterOn}
            onCheckedChange={(on) => (on ? void changeMaster(false) : setConfirmMasterOff(true))}
            disabled={!canEdit || busy !== null || confirmMasterOff}
            aria-label={t('notificationModules.master.title')}
            data-testid="e13-master-switch"
          />
        </div>
        {canEdit && confirmMasterOff && (
          <div
            className="mt-4 flex flex-col gap-3 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-between"
            data-testid="e13-master-confirm"
          >
            <p className="text-sm text-foreground">{t('notificationModules.master.confirm')}</p>
            <div className="flex shrink-0 items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => setConfirmMasterOff(false)} disabled={busy === 'master'}>
                {t('Cancel')}
              </Button>
              <Button
                variant="destructive"
                size="sm"
                loading={busy === 'master'}
                onClick={() => void changeMaster(true)}
                data-testid="e13-master-confirm-off"
              >
                {t('notificationModules.master.confirmOff')}
              </Button>
            </div>
          </div>
        )}
        {rowError?.key === 'master' && (
          <p role="alert" className="mt-3 text-sm text-destructive">{rowError.message}</p>
        )}
      </section>

      <section aria-labelledby="e13-modules-title" className="space-y-3">
        <div className="space-y-1">
          <h3 id="e13-modules-title" className="text-sm font-semibold text-foreground">
            {t('notificationModules.list.title')}
          </h3>
          <p className="max-w-[70ch] text-sm text-muted-foreground">{t('notificationModules.list.hint')}</p>
        </div>

        <ul className={cn('divide-y divide-border rounded-lg border border-border', !masterOn && 'opacity-60')}>
          {data.modules.map((module) => {
            const title = moduleTitle(t, module.key);
            const isOpen = expanded.has(module.key);
            const newCount = module.types.filter((x) => x.isNewSinceAnswer).length;
            const stateKey =
              module.promptMode === 'review' ? 'reviewPending' : module.state === 'NotAsked' ? 'notAsked' : module.state === 'On' ? 'on' : 'off';
            // A module that is On but still waiting for an answer (E13 backfill "review", or types added since
            // the last answer "newTypes"). The switch is already checked, so it can only send Off; this is the
            // one way to record an On answer here besides the inline prompt (HR only; Admin sees this read-only).
            // Re-answering On stamps AnsweredOn, which clears "review" and opens the new types.
            const confirmAction =
              module.state === 'On' && module.promptMode === 'review'
                ? t('notificationModules.prompt.keepOn')
                : module.state === 'On' && module.promptMode === 'newTypes' && newCount > 0
                  ? t('notificationModules.list.turnOnNewTypes')
                  : null;
            return (
              <li key={module.key} data-testid={`e13-module-row-${module.key}`} data-state={module.state}>
                <div className="flex items-start justify-between gap-4 px-4 py-3.5">
                  <div className="min-w-0 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-foreground">{title}</span>
                      <span
                        className={cn(
                          'rounded-full px-2 py-0.5 text-xs font-medium',
                          module.state === 'On' ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                        )}
                        data-testid={`e13-module-state-${module.key}`}
                      >
                        {t(`notificationModules.state.${stateKey}`)}
                      </span>
                      {module.state === 'On' && newCount > 0 && (
                        <span className="rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-xs text-amber-800 dark:text-amber-300">
                          {t('notificationModules.list.newTypes', { count: newCount })}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">{moduleSummary(t, i18n.language, module.types)}</p>
                    {canEdit && confirmAction && (
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 pt-1">
                        <Button
                          variant="outline"
                          size="sm"
                          iconName="Check"
                          loading={busy === module.key}
                          disabled={busy !== null}
                          onClick={() => void changeModule(module, true)}
                          data-testid={`e13-module-confirm-${module.key}`}
                          data-mode={module.promptMode}
                        >
                          {confirmAction}
                        </Button>
                        <span className="text-xs text-muted-foreground">
                          {module.promptMode === 'review'
                            ? t('notificationModules.list.reviewHint')
                            : t('notificationModules.list.newTypesHint')}
                        </span>
                      </div>
                    )}
                    {module.answeredOn && module.answeredByName && (
                      <p className="text-xs text-muted-foreground">
                        {t('notificationModules.list.answeredBy', {
                          name: module.answeredByName,
                          date: new Date(module.answeredOn).toLocaleDateString(i18n.language),
                        })}
                      </p>
                    )}
                    <button
                      type="button"
                      onClick={() => toggleExpanded(module.key)}
                      aria-expanded={isOpen}
                      aria-controls={`e13-module-types-${module.key}`}
                      className="inline-flex items-center gap-1 rounded text-xs font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      {isOpen ? t('notificationModules.prompt.hideDetails') : t('notificationModules.prompt.showDetails')}
                      <AppIcon
                        name="ChevronDown"
                        size={12}
                        className={cn('transition-transform duration-200 motion-reduce:transition-none', isOpen && 'rotate-180')}
                      />
                    </button>
                    {rowError?.key === module.key && (
                      <p role="alert" className="text-xs text-destructive">{rowError.message}</p>
                    )}
                  </div>
                  <Switch
                    checked={module.state === 'On'}
                    onCheckedChange={(on) => void changeModule(module, on)}
                    disabled={!canEdit || busy !== null}
                    aria-label={title}
                    data-testid={`e13-module-switch-${module.key}`}
                  />
                </div>
                {isOpen && (
                  <ul id={`e13-module-types-${module.key}`} className="divide-y divide-border border-t border-border bg-muted/20">
                    {module.types.map((type) => (
                      <li
                        key={type.id}
                        className="flex flex-col gap-1 px-4 py-2 ps-8 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
                      >
                        <span className="text-sm text-foreground">{typeLabel(t, type.name)}</span>
                        <span className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                          <span className="rounded bg-muted px-1.5 py-0.5">{cadenceLabel(t, type.cadence)}</span>
                          {type.channels.map((c) => (
                            <span key={c} className="rounded border border-border px-1.5 py-0.5">
                              {channelLabel(t, c)}
                            </span>
                          ))}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </section>
    </div>
  );
};

export default NotificationModulesTab;
